/**
 * One string out of a per-language map, for whoever is READING it (NFR-006).
 *
 * The platform ships every language it publishes on the wire beside the one the
 * request was answered in, and the reader picks. That is what makes a language
 * switch a render rather than a round trip: the guest's own copy (`t()`) moved
 * instantly and the server-authored strings - product names, section headings,
 * a formule's bullets - did not move until a reissued token had been sent and
 * three reads asked again, so for about a second the two halves of one screen
 * were in two different languages.
 *
 * The rule was `nameFor` in `apps/api-worker/src/wire.ts` and served exactly one
 * reader, the worker itself. It is stated here instead because the browser is
 * now the second reader of the same maps, and two implementations of "which
 * string do I show" is the defect family this directory exists to close.
 *
 * The fallback is not a default so much as the record's own history: a line
 * placed before the map existed carries none, and a language the catalogue had
 * no copy for is absent from it. Both mean "the map cannot answer", and the
 * string the platform served is the only other thing that is true.
 *
 * Pure and I/O-free, and deliberately free of any DOM-only global: a projection
 * that reaches for `URL` or `fetch` compiles here and fails `packages/services`.
 */

/** A string per language, keyed by `Locale` (`LOCALES` in `packages/i18n`). */
export type ByLocale = Readonly<Record<string, string>>;

/** The same, for a field that is a LIST: a recipe (CAT-002). */
export type ListByLocale = Readonly<Record<string, readonly string[]>>;

/**
 * The named value when the map has a non-blank one for this reader; the served
 * string otherwise.
 *
 * A key that is present but blank counts as absent, so a curator who created a
 * locale entry and has not written the copy yet gets the served string rather
 * than an empty product name (CAT-002).
 */
export function textIn(
  locale: string,
  byLocale: ByLocale | null | undefined,
  fallback: string,
): string {
  if (byLocale == null) return fallback;
  // `Object.hasOwn`, not a bare index: these maps arrive off the wire, and
  // `textIn('toString', ...)` must answer the fallback rather than a function.
  const named = Object.hasOwn(byLocale, locale) ? byLocale[locale] : undefined;
  return typeof named === 'string' && named.trim() !== '' ? named : fallback;
}

/**
 * The same rule spelled for a list: a locale whose list is ABSENT OR EMPTY
 * falls back to the served one, never to no ingredients.
 *
 * Empty is treated as absent for the same reason a blank string is - an
 * ingredients heading over nothing is worse than the recipe in the language the
 * platform did have (`localizedList` in `packages/db` states the storage half
 * of this).
 */
export function listIn(
  locale: string,
  byLocale: ListByLocale | null | undefined,
  fallback: readonly string[],
): readonly string[] {
  if (byLocale == null) return fallback;
  const named = Object.hasOwn(byLocale, locale) ? byLocale[locale] : undefined;
  return Array.isArray(named) && named.length > 0 ? named : fallback;
}
